import React, { useEffect, useRef, useState } from "react";
// import "./jpg_png_style.css";
import { Box } from "./jpgPngStyle";
import { ToolsHeader } from "../components/ToolsHeader";
import { isInIframe } from "../../utils/is-in-iframe";
import { Container } from "react-bootstrap";

interface ConversionStatus {
  id: string;
  fileName: string;
  status: "pending" | "converting" | "completed" | "error";
  result: string | null;
}

const JpgToPngConverter: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [conversions, setConversions] = useState<ConversionStatus[]>([]);

  useEffect(() => {
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  }, [files]);
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files)
      .filter((file) => /^image\/jpe?g$/i.test(file.type))
      .filter((file) => file.size <= 5 * 1024 * 1024);

    handleFiles(droppedFiles);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    const selectedFiles = Array.from(e.target.files)
      .filter((file) => /^image\/jpe?g$/i.test(file.type))
      .filter((file) => file.size <= 5 * 1024 * 1024);

    handleFiles(selectedFiles);
  };

  const handleFiles = (newFiles: File[]) => {
    const newConversions: ConversionStatus[] = newFiles.map((file) => ({
      id: Math.random().toString(36).substr(2, 9),
      fileName: file.name,
      status: "pending",
      result: null,
    }));
    setFiles((prev) => [...prev, ...newFiles]);
    setConversions((prev) => [...prev, ...newConversions]);
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  };

  const getStatusText = (status: ConversionStatus["status"]) => {
    switch (status) {
      case "pending":
        return "Waiting...";
      case "converting":
        return "Converting...";
      case "completed":
        return "Completed";
      case "error":
        return "Error";
      default:
        return "";
    }
  };

  const convertToPng = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx?.drawImage(img, 0, 0);
        try {
          const pngData = canvas.toDataURL("image/png");
          resolve(pngData);
        } catch {
          reject(new Error("Failed to convert"));
        }
      };

      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = URL.createObjectURL(file);
    });
  };

  const handleConvert = async () => {
    const updatedConversions = [...conversions];

    for (let i = 0; i < updatedConversions.length; i++) {
      if (updatedConversions[i].status !== "pending") continue;

      updatedConversions[i].status = "converting";
      setConversions([...updatedConversions]);

      const file = files.find((f) => f.name === updatedConversions[i].fileName);
      if (!file) continue;

      try {
        const result = await convertToPng(file);
        updatedConversions[i].status = "completed";
        updatedConversions[i].result = result;
      } catch {
        updatedConversions[i].status = "error";
      }

      setConversions([...updatedConversions]);
    }
  };

  const handleDownloadAll = () => {
    conversions
      .filter((c) => c.status === "completed" && c.result)
      .forEach((c) => {
        const a = document.createElement("a");
        a.href = c.result!;
        a.download = c.fileName.replace(/\.[^/.]+$/, ".png");
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      });
  };

  return (
    <Box>
      <div
        className={`container container-jpg-png ${
          !isInIframe ? "defaultText" : "interDisplay"
        }`}
      >
        <div className="converter">
          {!isInIframe && (
            <ToolsHeader
              title="JPG to PNG Converter"
              description="Convert JPG images to PNG format quickly with zero quality loss
                and fast processing."
            />
          )}
          {/* <h1 className="title">JPG to PNG Converter</h1>
          <p className="description">
            Convert your JPG images to PNG format with no quality loss
          </p> */}
          <div className="format-info">
            <span className="badge">Supported Formats: JPG, JPEG</span>
            <span className="badge">Max File Size: 5MB</span>
          </div>

          <div
            className="dropzone"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
          >
            <svg
              className="dropzone-icon"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            <p className="dropzone-text">
              Drag and drop your JPG files here, or click to browse
            </p>
            <p className="dropzone-hint">
              Only JPG/JPEG files under 5MB are supported
            </p>
            <input
              type="file"
              accept="image/jpeg,image/jpg"
              multiple
              ref={fileInputRef}
              className="hidden"
              onChange={handleFileInput}
            />
          </div>

          {files.length > 0 && (
            <>
              <div className="preview-grid">
                {files.map((file, index) => (
                  <div key={index} className="preview-item">
                    <img
                      src={URL.createObjectURL(file)}
                      alt={file.name}
                      className="preview-image"
                    />
                    <div className="preview-overlay">
                      {getStatusText(conversions[index].status)}
                    </div>
                  </div>
                ))}
              </div>

              <div className="buttons">
                <button
                  className="button button-primary"
                  onClick={handleConvert}
                  disabled={!conversions.some((c) => c.status === "pending")}
                >
                  Convert to PNG
                </button>
                <button
                  className="button button-success"
                  onClick={handleDownloadAll}
                  style={{
                    display: conversions.some((c) => c.status === "completed")
                      ? "flex"
                      : "none",
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Download All
                </button>
              </div>
            </>
          )}
        </div>
      {/* </div> */}
      </div>
    </Box>
  );
};

export default JpgToPngConverter;
